import React, { useId } from 'react';

/** Options numériques par défaut (alignées demande produit). */
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Barre de pagination + sélecteur « Éléments par page ».
 *
 * @param {'top'|'bottom'} [placement='bottom'] — bordure visuelle (séparation avec le tableau).
 * @param {(n: number) => void} [onPageSizeChange] — si défini, affiche la liste `pageSizeOptions`.
 * @param {number[]} [pageSizeOptions]
 * @param {{ value: string, label: string }[]} [pageSizeChoices] — prioritaire sur `pageSizeOptions` (ex. option « Tous »).
 * @param {string} [pageSizeSelectValue] — valeur contrôlée du select si `pageSizeChoices` est utilisé.
 * @param {(value: string) => void} [onPageSizeSelectChange]
 */
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  placement = 'bottom',
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  pageSizeChoices,
  pageSizeSelectValue,
  onPageSizeSelectChange,
}) => {
  const selectId = useId();
  const tp = Math.max(1, Number(totalPages) || 1);
  const ti = Math.max(0, Number(totalItems) || 0);
  const ipp = Math.max(1, Number(itemsPerPage) || 1);
  const cp = Math.min(Math.max(1, Number(currentPage) || 1), tp);

  const hasCustomChoices =
    Array.isArray(pageSizeChoices) &&
    pageSizeChoices.length > 0 &&
    typeof onPageSizeSelectChange === 'function';

  const hasNumericPageSize = typeof onPageSizeChange === 'function';

  const showSizeSelect = hasCustomChoices || hasNumericPageSize;

  if (ti <= 0) return null;

  const showNav = tp > 1;
  const showBar = showNav || showSizeSelect;

  if (!showBar) return null;

  const startItem = (cp - 1) * ipp + 1;
  const endItem = Math.min(cp * ipp, ti);

  const borderClass =
    placement === 'top'
      ? 'border-b border-gray-200 rounded-t-xl'
      : 'border-t border-gray-200 rounded-b-xl';

  const renderPageSize = () => {
    if (hasCustomChoices) {
      const v =
        pageSizeSelectValue !== undefined && pageSizeSelectValue !== null
          ? String(pageSizeSelectValue)
          : String(ipp);
      return (
        <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
          <label htmlFor={selectId} className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Éléments par page
          </label>
          <select
            id={selectId}
            value={v}
            onChange={(e) => onPageSizeSelectChange(e.target.value)}
            className="min-h-[40px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-entrea-sky focus:outline-none focus:ring-2 focus:ring-entrea-royal/25"
          >
            {pageSizeChoices.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label != null ? opt.label : opt.value}
              </option>
            ))}
          </select>
        </div>
      );
    }
    if (hasNumericPageSize) {
      const sizes = pageSizeOptions.map((n) => Number(n)).filter((n) => n > 0);
      const safeSizes = sizes.length ? sizes : DEFAULT_PAGE_SIZE_OPTIONS;
      const effectiveSizes = safeSizes.includes(ipp)
        ? safeSizes
        : [...safeSizes, ipp].sort((a, b) => a - b);
      const current = effectiveSizes.includes(ipp)
        ? ipp
        : effectiveSizes.includes(20)
          ? 20
          : effectiveSizes[0];
      return (
        <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
          <label htmlFor={selectId} className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Éléments par page
          </label>
          <select
            id={selectId}
            value={String(current)}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (!Number.isFinite(next) || next < 1) return;
              onPageSizeChange(next);
            }}
            className="min-h-[40px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-entrea-sky focus:outline-none focus:ring-2 focus:ring-entrea-royal/25"
          >
            {effectiveSizes.map((n) => (
              <option key={n} value={String(n)}>
                {n}
              </option>
            ))}
          </select>
        </div>
      );
    }
    return null;
  };

  const summary = (
    <p className="text-sm text-gray-700">
      Affichage de <span className="font-medium">{ti === 0 ? 0 : startItem}</span> à{' '}
      <span className="font-medium">{endItem}</span> sur <span className="font-medium">{ti}</span> résultat
      {ti > 1 ? 's' : ''}
    </p>
  );

  const navDesktop = showNav ? (
    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, cp - 1))}
        disabled={cp === 1}
        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="sr-only">Précédent</span>
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {Array.from({ length: tp }, (_, i) => i + 1).map((page) => {
        if (
          page === 1 ||
          page === tp ||
          (page >= cp - 1 && page <= cp + 1)
        ) {
          return (
            <button
              key={page}
              type="button"
              onClick={() => onPageChange(page)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                page === cp
                  ? 'z-10 bg-entrea-royal text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-entrea-royal'
                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
              }`}
            >
              {page}
            </button>
          );
        }
        if (page === cp - 2 || page === cp + 2) {
          return (
            <span
              key={page}
              className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
            >
              ...
            </span>
          );
        }
        return null;
      })}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(tp, cp + 1))}
        disabled={cp >= tp}
        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="sr-only">Suivant</span>
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </nav>
  ) : null;

  return (
    <div className={`bg-white px-4 py-3 sm:px-6 ${borderClass}`}>
      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-2">
          {renderPageSize()}
          <div className={showSizeSelect ? 'sm:pl-0' : ''}>{summary}</div>
        </div>

        {showNav ? (
          <>
            <div className="flex flex-1 justify-between gap-3 lg:hidden">
              <button
                type="button"
                onClick={() => onPageChange(Math.max(1, cp - 1))}
                disabled={cp === 1}
                className="relative inline-flex min-h-[44px] flex-1 touch-manipulation items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                type="button"
                onClick={() => onPageChange(Math.min(tp, cp + 1))}
                disabled={cp >= tp}
                className="relative inline-flex min-h-[44px] flex-1 touch-manipulation items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden lg:block">{navDesktop}</div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Pagination;
